import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { canExportDashboardPdf } from 'Helpers/PermissionsHelper';
import F2WCustomLogger from 'Services/F2WCustomLogger';

/**
 * Generates a PDF from the dashboard container element.
 * Captures the DOM via html2canvas, adds a header with season/date/user,
 * and triggers a file download.
 */
const generatePdf = async (dashboardElement, season, userName) => {
  const PDF_MARGIN = 20;
  const HEADER_HEIGHT = 30;

  const canvas = await html2canvas(dashboardElement, {
    scale: 1.5,
    useCORS: true,
    logging: false,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.7);
  const imgWidth = 210 - PDF_MARGIN * 2;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageHeight = 297 - PDF_MARGIN * 2;

  // Header
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text('F2W Dashboard Report', PDF_MARGIN, PDF_MARGIN);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Season: ${season}`, PDF_MARGIN, PDF_MARGIN + 6);
  pdf.text(`Exported by: ${userName}`, PDF_MARGIN, PDF_MARGIN + 11);
  pdf.text(`Export date: ${new Date().toLocaleDateString()}`, PDF_MARGIN, PDF_MARGIN + 16);

  // Dashboard content — paginate if needed
  let yOffset = 0;
  const contentStartY = PDF_MARGIN + HEADER_HEIGHT;
  const availableFirstPage = pageHeight - HEADER_HEIGHT;

  while (yOffset < imgHeight) {
    if (yOffset > 0) {
      pdf.addPage();
    }

    const currentPageAvailable = yOffset === 0 ? availableFirstPage : pageHeight;
    const startY = yOffset === 0 ? contentStartY : PDF_MARGIN;

    pdf.addImage(imgData, 'JPEG', PDF_MARGIN, startY - yOffset, imgWidth, imgHeight);

    yOffset += currentPageAvailable;
  }

  const fileName = `F2W_Dashboard_${season}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(fileName);
};

const PdfExportButton = ({ season, userName, dashboardRef, userRoles }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleExport = useCallback(async () => {
    if (!dashboardRef?.current) return;

    setIsGenerating(true);
    try {
      await generatePdf(dashboardRef.current, season, userName);
    } catch (error) {
      F2WCustomLogger.error('PDF export failed', error, { view: 'Dashboard' });
    } finally {
      setIsGenerating(false);
    }
  }, [dashboardRef, season, userName]);

  if (!canExportDashboardPdf(userRoles)) {
    return null;
  }

  return (
    <Button
      variant="outline-primary"
      size="sm"
      onClick={handleExport}
      disabled={isGenerating}
      aria-label="Export dashboard to PDF"
    >
      {isGenerating ? (
        <React.Fragment>
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
          Generating PDF...
        </React.Fragment>
      ) : (
        <React.Fragment>
          <i className="fa fa-file-pdf-o me-2" />
          Export to PDF
        </React.Fragment>
      )}
    </Button>
  );
};

PdfExportButton.propTypes = {
  season: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  dashboardRef: PropTypes.shape({
    current: PropTypes.instanceOf(typeof Element !== 'undefined' ? Element : Object),
  }).isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PdfExportButton;
